import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, HelpCircle, MessageSquare, ThumbsUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import './SupportAndFeedback.css';

const API_BASE_URL = API_URL;

interface FormData {
  subject: string;
  message: string;
  image: File | null;
}

interface FeedbackData {
  name: string;
  email: string;
  rating: number;
  category: string;
  feedback: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

const SupportAndFeedback: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });

  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(0);

  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem("theme");
      const newIsDarkMode = savedTheme === "dark";
      setIsDarkMode(newIsDarkMode);
      document.body.classList.toggle("dark", newIsDarkMode);
    };

    checkTheme();
    window.addEventListener("storage", checkTheme);
    const interval = setInterval(checkTheme, 100);

    return () => {
      window.removeEventListener("storage", checkTheme);
      clearInterval(interval);
    };
  }, []);

  const [supportForm, setSupportForm] = useState<FormData>({
    subject: '',
    message: '',
    image: null
  });

  const [feedbackForm, setFeedbackForm] = useState<FeedbackData>({
    name: '',
    email: '',
    rating: 0,
    category: '',
    feedback: ''
  });

  const [isSubmittingSupport, setIsSubmittingSupport] = useState<boolean>(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState<boolean>(false);
  const [supportSuccess, setSupportSuccess] = useState<boolean>(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState<boolean>(false);

  const faqs: FAQItem[] = [
    {
      question: "Can I upload my own sketches, existing blueprints, or reference images?",
      answer: "Yes. You can upload existing plans, hand-drawn sketches, or images directly into the prompt box. Sooru.ai can use these as a foundation for design modification or full concept generation."
    },
    {
      question: "How does Sooru.AI ensure design compliance with building codes?",
      answer: "Sooru.AI integrates with regional building codes and regulations to automatically validate your designs. Our AI checks for compliance issues and suggests modifications to meet local requirements."
    },
    {
      question: "Can I collaborate with my team on the same project?",
      answer: "Yes! Sooru.AI supports real-time collaboration. You can invite team members to view and edit projects simultaneously, with changes synced instantly across all participants."
    },
    {
      question: "What file formats does Sooru.AI support for export?",
      answer: "We support a wide range of formats including DWG, DXF, PDF, PNG, and various 3D formats like OBJ and FBX. You can also export directly to popular CAD software."
    }
  ];

  const emojiRatings = [
    { emoji: '😟', value: 1, label: 'Very Dissatisfied' },
    { emoji: '😕', value: 2, label: 'Dissatisfied' },
    { emoji: '😐', value: 3, label: 'Neutral' },
    { emoji: '🙂', value: 4, label: 'Satisfied' },
    { emoji: '😄', value: 5, label: 'Very Satisfied' }
  ];

  const handleSupportSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmittingSupport(true);

    try {
      const userProfileRaw = localStorage.getItem("userProfile");
      if (!userProfileRaw) {
        window.location.href = "/login";
        return;
      }

      const userProfile = JSON.parse(userProfileRaw);
      const formData = new FormData();

      formData.append("subject", supportForm.subject);
      formData.append("message", supportForm.message);
      if (supportForm.image) {
        const imageBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(supportForm.image as File);
        });
        formData.append("image", imageBase64);
      }
      formData.append("name", `${userProfile.first_name || ""} ${userProfile.last_name || ""}`);
      formData.append("email", userProfile.email || "");
      const response = await fetch(`${API_BASE_URL}/support`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to submit support request");

      setSupportSuccess(true);
      setSupportForm({ subject: "", message: "", image: null });

      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      console.error(err);
      alert("There was an error submitting your request.");
      setIsSubmittingSupport(false);
    }
  };

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmittingFeedback(true);

    try {
      const userProfileRaw = localStorage.getItem("userProfile");
      if (!userProfileRaw) {
        window.location.href = "/login";
        return;
      }

      const userProfile = JSON.parse(userProfileRaw);
      const payload = {
        name: `${userProfile.first_name || ""} ${userProfile.last_name || ""}`,
        email: userProfile.email || "",
        rating: feedbackForm.rating,
        category: feedbackForm.category,
        feedback: feedbackForm.feedback,
      };

      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to submit feedback");

      setFeedbackSuccess(true);
      setFeedbackForm({ name: "", email: "", rating: 0, category: "", feedback: "" });

      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      console.error(err);
      alert("There was an error submitting your feedback.");
      setIsSubmittingFeedback(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSupportForm(prev => ({ ...prev, image: file }));
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`sf-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Hero Section */}
      <section className="sf-hero">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          We've Got Your<br />
          <span className="sf-highlight">Problems Covered.</span>
        </motion.h1>
        <motion.p
          className="sf-hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          We're here to help with any questions or<br />
          always happy to hear your feedback!
        </motion.p>
      </section>

      {/* Breadcrumb */}
      <div className="sf-breadcrumb">
        <a href="/">Home</a> / <span>Support & Feedback</span>
      </div>

      {/* How Can We Help Section */}
      <section className="sf-help-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          How Can We Help You?
        </motion.h2>

        <div className="sf-help-cards">
          <motion.div
            className="sf-help-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onClick={() => scrollToSection('support-section')}
          >
            <div className="sf-help-icon">
              <HelpCircle size={28} />
            </div>
            <h3>Get Help</h3>
            <p>We provide immediate access to problem resolution.</p>
          </motion.div>

          <motion.div
            className="sf-help-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onClick={() => scrollToSection('faq-section')}
          >
            <div className="sf-help-icon">
              <MessageSquare size={28} />
            </div>
            <h3>FAQs</h3>
            <p>Get instant answers for all your platform questions.</p>
          </motion.div>

          <motion.div
            className="sf-help-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            onClick={() => scrollToSection('feedback-section')}
          >
            <div className="sf-help-icon">
              <ThumbsUp size={28} />
            </div>
            <h3>Feedback</h3>
            <p>Submit your insights to help us build a better platform.</p>
          </motion.div>
        </div>
      </section>

      {/* Get Help & Support Section */}
      <section id="support-section" className="sf-support-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Get Help & Support
        </motion.h2>

        <motion.div
          className="sf-form-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {supportSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="sf-success-message"
            >
              Support request submitted successfully! We'll get back to you soon.
            </motion.div>
          )}

          <form onSubmit={handleSupportSubmit}>
            <div className="sf-form-field">
              <label htmlFor="support-subject">Subject*</label>
              <select
                id="support-subject"
                required
                value={supportForm.subject}
                onChange={(e) => setSupportForm(prev => ({ ...prev, subject: e.target.value }))}
              >
                <option value="">Select a Category</option>
                <option value="technical-issue">Technical Issue</option>
                <option value="account-issue">Account Issue</option>
                <option value="billing">Billing</option>
                <option value="feature-request">Feature Request</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="sf-form-field">
              <label htmlFor="support-message">Message*</label>
              <textarea
                id="support-message"
                required
                rows={5}
                value={supportForm.message}
                onChange={(e) => setSupportForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Please provide details information about your issue."
                maxLength={500}
              />
              <span className="sf-char-count">{supportForm.message.length}/500</span>
            </div>

            <div className="sf-form-field">
              <label>Attach Screenshot</label>
              <div className="sf-upload-area">
                <input
                  type="file"
                  id="support-image"
                  accept="image/*"
                  onChange={handleImageUpload}
                  hidden
                />
                <label htmlFor="support-image" className="sf-upload-label">
                  <UploadFileIcon sx={{ fontSize: 32, color: isDarkMode ? '#7c9dd1' : '#2d3e96' }} />
                  <span><strong>Upload an Image</strong></span>
                  <span className="sf-upload-hint"></span>
                </label>
              </div>
              {supportForm.image && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Chip
                    label={supportForm.image.name}
                    onDelete={() => setSupportForm(prev => ({ ...prev, image: null }))}
                    sx={{ 
                      backgroundColor: isDarkMode ? '#3d4451' : '#dfe4ff',
                      color: isDarkMode ? '#fff' : '#2d3e96'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#b8c5d6' : '#666' }}>
                    {Math.round((supportForm.image.size || 0) / 1024)} KB
                  </Typography>
                </Stack>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmittingSupport}
              className="sf-submit-btn"
            >
              {isSubmittingSupport ? (
                <span className="sf-btn-loading">
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  Submitting...
                </span>
              ) : 'Submit Support Request'}
            </button>
          </form>
        </motion.div>
      </section>

      {/* FAQs Section */}
      <section id="faq-section" className="sf-faq-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Frequently Asked Questions (FAQs)
        </motion.h2>

        <div className="sf-faq-list">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className={`sf-faq-item ${expandedFAQ === index ? 'expanded' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <button
                className="sf-faq-question"
                onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
              >
                <span>Q{index + 1}. {faq.question}</span>
                {expandedFAQ === index ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>
              {expandedFAQ === index && (
                <motion.div
                  className="sf-faq-answer"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  {faq.answer}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Feedback Section */}
      <section id="feedback-section" className="sf-feedback-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          We Value Your Opinion
        </motion.h2>

        <motion.div
          className="sf-form-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {feedbackSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="sf-success-message"
            >
              Thank you for your valuable feedback! We appreciate your input.
            </motion.div>
          )}

          <form onSubmit={handleFeedbackSubmit}>
            <div className="sf-form-field">
              <label>Overall Rating</label>
              <div className="sf-emoji-rating">
                {emojiRatings.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className={`sf-emoji-btn ${feedbackForm.rating === item.value ? 'selected' : ''}`}
                    onClick={() => setFeedbackForm(prev => ({ ...prev, rating: item.value }))}
                    title={item.label}
                  >
                    {item.emoji}
                  </button>
                ))}
              </div>
            </div>

            <div className="sf-form-field">
              <label htmlFor="feedback-category">Subject*</label>
              <select
                id="feedback-category"
                required
                value={feedbackForm.category}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">Select a category</option>
                <option value="user-experience">User Experience</option>
                <option value="features">Features & Functionality</option>
                <option value="performance">Performance</option>
                <option value="bug-report">Bug Report</option>
                <option value="feature-request">Feature Request</option>
                <option value="general">General Feedback</option>
              </select>
            </div>

            <div className="sf-form-field">
              <label htmlFor="feedback-message">Your Feedback</label>
              <textarea
                id="feedback-message"
                rows={5}
                value={feedbackForm.feedback}
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, feedback: e.target.value }))}
                placeholder="Share your thoughts, suggestions or experiences with us."
                maxLength={500}
              />
              <span className="sf-char-count">{feedbackForm.feedback.length}/500</span>
            </div>

            <button
              type="submit"
              disabled={isSubmittingFeedback || feedbackForm.rating === 0}
              className="sf-submit-btn"
            >
              {isSubmittingFeedback ? (
                <span className="sf-btn-loading">
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  Submitting...
                </span>
              ) : 'Submit Feedback'}
            </button>
          </form>
        </motion.div>
      </section>
    </div>
  );
};

export default SupportAndFeedback;